// AGC: Field/lot dropdown — confidence-sorted list with auto-select and "Create new field" option
import React, { useEffect } from 'react'
import { Check, Plus } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { VoiceFieldProps } from './types'

export const FieldSelector: React.FC<VoiceFieldProps> = ({
  info,
  inputValues,
  setInputValues,
  wizardMode,
}) => {
  const { t } = useTranslation()

  // AGC: Sort suggestions: highest confidence first, then alphabetically
  const sorted = [...(info.suggestions || [])].sort((a: { confidence: number; label: string }, b: { confidence: number; label: string }) => {
    if (a.confidence !== b.confidence) return b.confidence - a.confidence
    return a.label.localeCompare(b.label)
  })

  // AGC: Auto-select the best match if confidence >= 80 and nothing selected yet
  // AGC: Uses useEffect to avoid setState during render (replaces original setTimeout approach)
  useEffect(() => {
    if (!inputValues.field && sorted.length > 0 && (sorted[0]?.confidence ?? 0) >= 80) {
      setInputValues(prev => {
        if (!prev.field) return { ...prev, field: sorted[0]?.value ?? '', fieldName: sorted[0]?.label ?? '' }
        return prev
      })
    }
    // AGC: Only run on mount or when suggestions change — inputValues.field excluded intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.suggestions])

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', btn: 'px-4 py-3.5 text-base', checkIcon: 'w-5 h-5', plusIcon: 'w-5 h-5' }
    : { card: 'p-2', label: 'text-xs', btn: 'px-2 py-1 text-xs', checkIcon: 'w-3 h-3', plusIcon: 'w-3 h-3' }

  return (
    <div className={`${sz.card} bg-white/5 border border-white/20 rounded-xl`}>
      <div className="mb-3">
        <span className={`text-white/80 ${sz.label} font-medium flex items-center gap-1`}>
          🗺️ {info.fieldLabel}
        </span>
      </div>
      <div className="space-y-0.5 max-h-28 overflow-y-auto">
        {sorted.map((s: { value: string; label: string; confidence: number }) => (
          <button
            key={s.value}
            onClick={() => setInputValues(prev => ({ ...prev, field: s.value, fieldName: s.label }))}
            className={`w-full flex items-center justify-between ${sz.btn} rounded text-left transition-colors ${
              inputValues.field === s.value
                ? 'bg-emerald-500/30 border border-emerald-500/50 text-emerald-300 font-medium'
                : s.confidence > 90
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 hover:bg-emerald-500/20'
                  : 'bg-white/5 hover:bg-white/10 text-white/80 border border-transparent'
            }`}
          >
            <span className="truncate">{s.label}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {s.confidence >= 80 && <span className="text-emerald-400 text-[10px]">⭐</span>}
              {inputValues.field === s.value && <Check className={sz.checkIcon} />}
            </div>
          </button>
        ))}

        {/* AGC: Option to create a new field */}
        {info.allowNewField && (
          <button
            onClick={() => setInputValues(prev => ({ ...prev, field: '__new__', fieldName: '' }))}
            className={`w-full flex items-center gap-2 ${sz.btn} rounded text-left transition-colors ${
              inputValues.field === '__new__'
                ? 'bg-blue-500/30 border border-blue-500/50 text-blue-300 font-medium'
                : 'bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-300'
            }`}
          >
            <Plus className={sz.plusIcon} />
            {t('voice.createNewField')}
          </button>
        )}
      </div>

      {/* AGC: Selected field confirmation pill */}
      {inputValues.field && inputValues.field !== '__new__' && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-300 bg-emerald-500/10 rounded px-2 py-0.5">
          <Check className={sz.checkIcon} />
          {t('voice.selectedField')}: <strong>{inputValues.fieldName || inputValues.field}</strong>
        </div>
      )}
    </div>
  )
}
